import type { Hero, Media } from 'types';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import camelcaseKeys from 'camelcase-keys';
import { Wrap } from '@leafwell/components';
import HeadingBody from 'components/heading-body';
import { request } from 'lib/rest';

import SignupFormPasswordless from './signup-form-passwordless';
import HeroBenefits from './hero-benefits';

type PartnerProps = {
  title: string;
  logo: Media;
  website: string;
  couponText?: string;
};

type HomeHeroNewProps = Hero & {
  initialStateCode?: string | null;
};

const HERO_IMAGE = '/remove-signup-form-exp.png';

const HomeHeroNew: React.FC<HomeHeroNewProps> = ({
  title,
  description,
  formSubmitText,
  initialStateCode,
}) => {
  const { query } = useRouter();
  const [partnerData, setPartnerData] = useState<PartnerProps | null>(null);
  const [currentState, setCurrentState] = useState<string | undefined>(
    initialStateCode ?? undefined,
  );

  const submitText = formSubmitText || 'Get your card';

  useEffect(() => {
    if (query.utm_source && !partnerData) {
      // call api to fetch partner data based on `utm_source`
      request(`/partner?slug=${query.utm_source}`).then(res => {
        if (res.data && res.data?.totalItems > 0) {
          const { title, acf } = camelcaseKeys(res.data.items[0], {
            deep: true,
          });
          const { logo, website, couponText, states } = acf;

          if (
            !states.length ||
            (states.length && states.includes(currentState))
          ) {
            setPartnerData({
              title,
              logo,
              website,
              couponText,
            });
          }
        }
      });
    }
  }, [query]);

  const signupForm = (
    <>
      <div id="hero-cta-sentinel">
        <SignupFormPasswordless
          stateCode={currentState}
          formSubmitText={submitText}
          ctaLocation="hero-homepage"
          redirectOnStateChange={false}
          onStateChange={setCurrentState}
        />
      </div>
      <HeroBenefits
        stateCode={currentState}
        placement="mobile"
        className="lg:hidden"
      />
    </>
  );

  return (
    <section className="relative grid bg-lilac-50 xl:-mt-10 lw-state-pages">
      <div className="hidden lg:block absolute inset-y-0 right-0 w-[70%] 3xl:w-[60%] pointer-events-none">
        <Image
          src={HERO_IMAGE}
          alt=""
          fill
          className="object-cover object-top"
          priority
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(100deg, #f5f1fd 10%, #f5f1fdcc 20%, transparent 65%)',
          }}
        />
      </div>
      <Wrap className="items-start z-10 relative" id="leafwell-signup-form">
        <div
          className={`flex flex-col gap-y-4 lg:gap-y-8 text-pretty lg:max-w-2xl ${
            partnerData ? '' : 'lg:pb-10'
          }`}
        >
          <HeadingBody
            level="1"
            title={title}
            className="font-semibold text-black"
          />
          <div className="block relative h-44 lg:hidden w-full rounded-2xl overflow-hidden">
            <Image
              src={HERO_IMAGE}
              alt=""
              width={600}
              height={400}
              className="w-full object-cover"
              priority
            />
            <div className="inline-flex absolute bottom-2 right-2 z-10 items-center gap-x-2 bg-[#F5F1FDE5] opacity-90 rounded-lg px-3 py-1 shadow-sm text-sm font-medium">
              <span className="text-green-500 text-lg">★</span>
              Trusted By{' '}
              <span className="text-purple-500 font-semibold">
                1 Million
              </span>{' '}
              Monthly Users
            </div>
          </div>
          {description?.length > 0 ? (
            <div
              className="!text-lg lg:!text-2xl text-black"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : null}
          {partnerData ? (
            <div className="grid transition-opacity duration-500 gap-4 p-4 bg-[#F6FEF9] border border-green-500 rounded-lg w-fit">
              <div className="inline-flex items-center">
                <div className="flex flex-col justify-center">
                  {!partnerData?.couponText ? (
                    <p className="text text-pretty text-lg">
                      Leafwell is proud to partner <br />
                      with <strong>{partnerData.title}</strong>
                    </p>
                  ) : null}
                  {partnerData?.couponText ? (
                    <div
                      className="inline-block"
                      dangerouslySetInnerHTML={{
                        __html: partnerData.couponText,
                      }}
                    />
                  ) : null}
                </div>
                {partnerData?.logo ? (
                  <div className="w-32 flex items-center justify-center">
                    <Image
                      src={partnerData.logo.src}
                      alt={partnerData.logo.alt || ''}
                      width={100}
                      height={100}
                    />
                  </div>
                ) : null}
              </div>
              {signupForm}
            </div>
          ) : (
            <div className="flex flex-col gap-y-4">{signupForm}</div>
          )}
        </div>
        <div className="hidden lg:flex absolute bottom-6 right-6 z-10 items-center gap-x-2 bg-[#F5F1FDE5] opacity-90 rounded-lg px-3 py-1 shadow-sm text-sm font-medium">
          <span className="text-green-500 text-lg">★</span>
          Trusted By{' '}
          <span className="text-purple-500 font-semibold">1 Million</span>{' '}
          Monthly Users
        </div>
      </Wrap>
      <HeroBenefits
        stateCode={currentState}
        placement="desktop"
        className="hidden lg:block absolute right-10 bottom-[104px] z-20"
      />
    </section>
  );
};

export default HomeHeroNew;
